import cn from 'classnames';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useEffect } from 'react';

import { MenuItem } from '@/types/menu';

import MenuDropdown from './MenuDropdown';
import MenuItemButton from './MenuItemButton';
import MenuItemLink from './MenuItemLink';

type Props = {
  item: MenuItem;
  activeMenu?: string;
  activeSubMenu?: string;
  onMenuToggle: (itemId: string | undefined) => void;
  onSubMenuToggle: (itemId: string | undefined) => void;
  onLinkClick: () => void;
};

const TopLevelMenuItem: React.FC<Props> = ({
  item,
  activeMenu,
  activeSubMenu,
  onMenuToggle,
  onSubMenuToggle,
  onLinkClick
}) => {
  const router = useRouter();
  const menuRef = React.useRef<HTMLLIElement>(null);
  const hasChildren = Object.keys(item.items || {}).length > 0;
  const isOpen = activeMenu === item.id;

  const isActive = hasChildren
    ? !!Object.values(item?.items ?? {}).find(i => i.resource === router.route || i.resource === router.asPath)
    : item.resource === router.asPath || item.resource === router.route;

  useEffect(() => {
    if (!isOpen) return;

    // Check if device is touch-enabled (mobile/tablet)
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    const handleClickOutside = (event: MouseEvent) => {
      // Only apply click-outside behavior on non-touch devices (desktop)
      if (isTouchDevice) return;

      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onMenuToggle(undefined);
        onSubMenuToggle(undefined);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onMenuToggle(undefined);
        onSubMenuToggle(undefined);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onMenuToggle, onSubMenuToggle]);

  // availableFrom is Unix timestamp in seconds, Date.now() is in milliseconds
  if (item.options.availableFrom && item.options.availableFrom * 1000 > Date.now()) {
    return null;
  }

  return (
    <li
      ref={menuRef}
      className={cn(item.options.classes ?? '', {
        open: isOpen,
        active: isActive,
        'has-children': hasChildren
      })}
    >
      {hasChildren ? (
        <>
          <MenuItemButton
            item={item}
            isOpen={isOpen}
            onClick={() => onMenuToggle(isOpen ? undefined : item.id)}
            nameClassName="name"
          />
          {item.items && (
            <ul className="dropdown" aria-hidden={!isOpen}>
              {Object.values(item.items).map(subItem => {
                // availableFrom is Unix timestamp in seconds, Date.now() is in milliseconds
                if (subItem.options.availableFrom && subItem.options.availableFrom * 1000 > Date.now()) {
                  return null;
                }

                const hasSubChildren = Object.keys(subItem.items || {}).length > 0;
                const isSubActive = hasSubChildren
                  ? !!Object.values(subItem?.items ?? {}).find(
                      i => i.resource === router.route || i.resource === router.asPath
                    )
                  : subItem.resource === router.asPath || subItem.resource === router.route;

                return (
                  <li
                    key={subItem.id}
                    className={cn(subItem.options.classes ?? '', {
                      open: activeSubMenu === subItem.id,
                      active: isSubActive,
                      'has-children': hasSubChildren
                    })}
                  >
                    {hasSubChildren ? (
                      <>
                        <MenuItemButton
                          item={subItem}
                          isOpen={activeSubMenu === subItem.id}
                          tabIndex={isOpen ? 0 : -1}
                          ariaHidden={!isOpen}
                          onClick={() => onSubMenuToggle(activeSubMenu === subItem.id ? undefined : subItem.id)}
                          nameClassName="subname"
                        />
                        {subItem.items && (
                          <MenuDropdown
                            items={Object.values(subItem.items)}
                            activeItem={activeSubMenu}
                            onItemToggle={onSubMenuToggle}
                            tabIndex={isOpen && activeSubMenu === subItem.id ? 0 : -1}
                            ariaHidden={activeSubMenu !== subItem.id}
                            nameClassName="subname"
                            dropdownClassName="sub-dropdown"
                          />
                        )}
                      </>
                    ) : (
                      <MenuItemLink
                        item={subItem}
                        tabIndex={isOpen ? 0 : -1}
                        ariaHidden={!isOpen}
                        nameClassName="subname"
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      ) : (
        <MenuItemLink item={item} onClick={onLinkClick} nameClassName="name" />
      )}
    </li>
  );
};

export default TopLevelMenuItem;
